import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitPath} from '@github-ui/paths'
import {Link, RelativeTime} from '@primer/react'
import type {ReactNode} from 'react'

import {useLayoutQuery} from '../../../../page-data/loaders/use-layout-query'
import {ActorLink} from '../../../ActorLink'
import {isForYouRefreshEnabled} from '../shared/for-you-feature-flags'
import styles from './MergedDescription.module.css'

/**
 * Renders a sentence describing who merged a pull request, the merge commit, and when.
 * Any content passed as `trailing` is placed inside the same inline-flex container so
 * additional sentences (e.g. a follow-up "Delete the branch to tidy up.") stay visually
 * aligned with the merge metadata.
 */
export function MergedDescription({trailing}: {trailing?: ReactNode}) {
  const {pullRequest, repository} = useLayoutQuery().data
  const {mergedBy, mergedByName, mergedByAvatarUrl, mergedTime, mergeCommitSha} = pullRequest
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const isRefreshEnabled = isForYouRefreshEnabled()

  const relativeMergedTime = mergedTime ? <RelativeTime datetime={mergedTime} /> : null

  const mergerLink = mergedBy ? (
    <ActorLink
      actor={{login: mergedBy, avatarUrl: mergedByAvatarUrl, displayName: mergedByName}}
      inline
      showAvatar={!isRefreshEnabled}
      className={isRefreshEnabled ? styles.Merger : undefined}
    />
  ) : null

  const commitLink =
    mergeCommitSha && repository?.name ? (
      <Link
        inline
        href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: mergeCommitSha})}
        {...getHovercardAttributesForType('commit', {
          commitish: mergeCommitSha,
          owner: repository.ownerLogin,
          repo: repository.name,
        })}
      >
        <code>{mergeCommitSha.slice(0, 7)}</code>
      </Link>
    ) : null

  if (!mergerLink && !commitLink) {
    return (
      <span className="d-inline-flex flex-items-center flex-wrap gap-1">
        <span className="d-inline-flex flex-items-center flex-wrap gap-1">
          This pull request was merged
          {relativeMergedTime ? <> {relativeMergedTime}</> : null}
          <span className="ml-n1">.</span>
        </span>
        {trailing}
      </span>
    )
  }

  return (
    <span className="d-inline-flex flex-items-center flex-wrap gap-1">
      <span className="d-inline-flex flex-items-center flex-wrap gap-1">
        {mergerLink ? (
          <>
            {mergerLink} merged
            {commitLink ? <> in {commitLink}</> : null}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span className="ml-n1">.</span>
          </>
        ) : (
          <>
            Merged in {commitLink}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span className="ml-n1">.</span>
          </>
        )}
      </span>
      {trailing}
    </span>
  )
}
